import { useLayoutEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/utils/utils';
import type { TourPlacement, TourStep } from '../types';

interface TourTooltipProps {
  step: TourStep;
  rect: DOMRect | null;
  /** Display name shown above the title (e.g. tour name). */
  tourName: string;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}

type ResolvedPlacement = Exclude<TourPlacement, 'auto'>;

const TOOLTIP_GAP = 12;
const VIEWPORT_MARGIN = 12;

interface Position {
  top: number;
  left: number;
  placement: ResolvedPlacement;
}

function resolvePlacement(
  rect: DOMRect,
  preferred: TourPlacement | undefined,
  tooltipSize: { width: number; height: number },
  viewport: { width: number; height: number }
): ResolvedPlacement {
  const space = {
    top: rect.top,
    bottom: viewport.height - rect.bottom,
    left: rect.left,
    right: viewport.width - rect.right,
  };

  const fits: Record<ResolvedPlacement, boolean> = {
    top: space.top >= tooltipSize.height + TOOLTIP_GAP + VIEWPORT_MARGIN,
    bottom: space.bottom >= tooltipSize.height + TOOLTIP_GAP + VIEWPORT_MARGIN,
    left: space.left >= tooltipSize.width + TOOLTIP_GAP + VIEWPORT_MARGIN,
    right: space.right >= tooltipSize.width + TOOLTIP_GAP + VIEWPORT_MARGIN,
  };

  if (preferred && preferred !== 'auto' && fits[preferred]) return preferred;

  const order: ResolvedPlacement[] = ['bottom', 'top', 'right', 'left'];
  const first = order.find((p) => fits[p]);
  if (first) return first;

  // Nothing fits cleanly — pick the side with the most space.
  const ranked = (Object.keys(space) as ResolvedPlacement[]).sort(
    (a, b) => space[b] - space[a]
  );
  return ranked[0];
}

function computePosition(
  rect: DOMRect,
  placement: ResolvedPlacement,
  tooltipSize: { width: number; height: number },
  viewport: { width: number; height: number }
): Position {
  let top = 0;
  let left = 0;

  switch (placement) {
    case 'top':
      top = rect.top - tooltipSize.height - TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipSize.width / 2;
      break;
    case 'bottom':
      top = rect.bottom + TOOLTIP_GAP;
      left = rect.left + rect.width / 2 - tooltipSize.width / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipSize.height / 2;
      left = rect.left - tooltipSize.width - TOOLTIP_GAP;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipSize.height / 2;
      left = rect.right + TOOLTIP_GAP;
      break;
  }

  // Clamp inside viewport.
  left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(left, viewport.width - tooltipSize.width - VIEWPORT_MARGIN)
  );
  top = Math.max(
    VIEWPORT_MARGIN,
    Math.min(top, viewport.height - tooltipSize.height - VIEWPORT_MARGIN)
  );

  return { top, left, placement };
}

export function TourTooltip({
  step,
  rect,
  tourName,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
}: TourTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  // Re-measure on every render where rect/step changes — the tooltip's own
  // size affects placement, so we need to measure after layout.
  useLayoutEffect(() => {
    if (!ref.current) return;
    const node = ref.current;
    const measure = () => {
      const tooltipSize = {
        width: node.offsetWidth,
        height: node.offsetHeight,
      };
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      if (!rect) {
        // No target — center the tooltip.
        setPosition({
          top: viewport.height / 2 - tooltipSize.height / 2,
          left: viewport.width / 2 - tooltipSize.width / 2,
          placement: 'bottom',
        });
        return;
      }
      const placement = resolvePlacement(rect, step.placement, tooltipSize, viewport);
      setPosition(computePosition(rect, placement, tooltipSize, viewport));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [rect, step.id, step.placement]);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`${tourName}: ${step.title}`}
      // Stop click + pointer events from bubbling out of the tour tooltip.
      //
      // The host app installs document-level outside-click handlers (e.g.
      // ``InteractiveViewerPage``'s ``handleClickOutside`` for the photo-
      // selection tooltip). Without this, clicking "Next" on a step whose
      // ``onBeforeNext`` opens such a popover would bubble to ``document``
      // *after* React flushes the popover state, immediately closing what
      // we just opened — and the tour would race past every step gated on
      // that popover.
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className={cn(
        'fixed z-[1001] w-80 max-w-[calc(100vw-24px)] rounded-lg border border-border',
        'bg-popover p-4 text-popover-foreground shadow-xl',
        'animate-in fade-in-0 zoom-in-95',
        // Force interactivity. Radix Dialog (modal) sets ``pointer-events:
        // none`` on body/siblings while it's open, and the tour tooltip is
        // portaled to ``body`` — without this the cursor falls through to
        // Pannellum behind the tooltip and Next/Back/Skip become un-clickable.
        'pointer-events-auto',
      )}
      // Inline backup in case a global ``pointer-events: none`` on a parent
      // node wins via specificity over the utility class.
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? 'visible' : 'hidden',
        pointerEvents: 'auto',
      }}
    >
      <div className="mb-3 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span className="shrink-0">{tourName}</span>
        <div
          role="progressbar"
          aria-label={`Tour progress: step ${stepIndex + 1} of ${totalSteps}`}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-valuenow={stepIndex + 1}
          className="h-1 flex-1 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{
              width: `${Math.min(100, ((stepIndex + 1) / totalSteps) * 100)}%`,
            }}
          />
        </div>
      </div>
      <h3 className="mb-2 text-base font-semibold leading-snug">{step.title}</h3>
      <div className="text-sm text-foreground/90 leading-relaxed">{step.body}</div>

      <div className="mt-4 flex items-center justify-between gap-2">
        {step.hideSkip ? (
          <span />
        ) : (
          <Button variant="ghost" size="sm" onClick={onSkip}>
            Skip tour
          </Button>
        )}
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button variant="outline" size="sm" onClick={onPrev}>
              Back
            </Button>
          )}
          {!step.requiresInteraction && (
            <Button size="sm" onClick={onNext}>
              {isLast ? 'Finish Tour' : 'Next'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
